import React, { useEffect, useMemo, useState } from 'react';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { Button, Checkbox, DatePicker, Form, Input, message, Modal, Select, Space, Table } from 'antd';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';
import {
  addClauseApi,
  addClauseVersionApi,
  deleteClauseApi,
  deleteClauseVersionApi,
  editClauseApi,
  getClauseDetailApi,
  getClauseListApi,
  getClauseVersionDetailApi,
  getClauseVersionListApi,
  publishClauseVersionApi,
  updateClauseVersionApi,
} from '@/api/member/clause';
import { state } from '@/store';
import '@wangeditor/editor/dist/css/style.css';
import { TERMS_KEY } from '@/router/Permissions';
import { defaultEditorConfig, defaultToolbarConfig } from '@/utils/editorConfig';
import { checkPermissions } from '@/utils/utils';

const { RangePicker } = DatePicker;

const TermsConditions: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const { currentShopPlatformType } = useSnapshot(state);
  const [createVisible, setCreateVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [editing, setEditing] = useState<any>(null);
  const [versionVisible, setVersionVisible] = useState(false);
  const [versionForm] = Form.useForm();
  const [versionEditor, setVersionEditor] = useState<any>(null);
  const [versionHtml, setVersionHtml] = useState('');
  const [versionTarget, setVersionTarget] = useState<any>(null);
  const [versionMode, setVersionMode] = useState<'create' | 'edit' | 'view'>('create');
  const [versionListVisible, setVersionListVisible] = useState(false);
  const [versionListLoading, setVersionListLoading] = useState(false);
  const [versionList, setVersionList] = useState<any[]>([]);
  const [versionPageNum, setVersionPageNum] = useState(1);
  const [versionPageSize, setVersionPageSize] = useState(10);
  const [versionTotal, setVersionTotal] = useState(0);
  const [versionFilterForm] = Form.useForm();
  const [versionClause, setVersionClause] = useState<any>(null);
  const toolbarConfig = useMemo(() => defaultToolbarConfig, []);
  const editorConfig = defaultEditorConfig;

  useEffect(() => {
    return () => {
      if (versionEditor) {
        versionEditor.destroy?.();
      }
    };
  }, [versionEditor]);

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id' },
      { title: '名称', dataIndex: 'clauseName' },
      { title: '描述', dataIndex: 'clausecDescription' },
      { title: '最新版本号', dataIndex: 'latestVersionId' },
      { title: '创建时间', dataIndex: 'createTime' },
      {
        title: '操作',
        render: (_: any, record: any) => (
          <Space size={12}>
            {checkPermissions(TERMS_KEY.CHECK_VERSION) && (
              <Button
                type="link"
                size="small"
                onClick={() => handleViewVersions(record)}>
                查看版本
              </Button>
            )}
            {checkPermissions(TERMS_KEY.NEW_VERSION) && (
              <Button
                type="link"
                size="small"
                onClick={() => handleCreateVersion(record)}>
                新建版本
              </Button>
            )}
            {checkPermissions(TERMS_KEY.EDIT) && (
              <Button
                type="link"
                size="small"
                onClick={() => handleEdit(record)}>
                编辑
              </Button>
            )}
            {checkPermissions(TERMS_KEY.DELETE) && (
              <Button
                type="link"
                size="small"
                danger
                onClick={() => handleDelete(record)}>
                删除
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [],
  );

  const fetchList = async (override?: { pageNum?: number; pageSize?: number }) => {
    const values = form.getFieldsValue();
    const params: any = {
      clauseName: values.clauseName || '',
      createTimeStart: values.createTime?.length ? dayjs(values.createTime[0]).format('YYYY-MM-DD HH:mm:ss') : '',
      createTimeEnd: values.createTime?.length ? dayjs(values.createTime[1]).format('YYYY-MM-DD HH:mm:ss') : '',
      pageNo: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
      platformType: currentShopPlatformType || '',
    };
    setLoading(true);
    try {
      const res = await getClauseListApi(params);
      if (res?.code === 200 || res?.code === 0) {
        setData(res.rows || res.data?.rows || []);
        setTotal(res.total || res.data?.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      } else {
        message.error(res?.msg || '获取列表失败');
      }
    } catch (error) {
      message.error('获取列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopPlatformType]);

  const handleReset = () => {
    form.resetFields();
    fetchList({ pageNum: 1 });
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      const payload = {
        clauseName: values.clauseName,
        clausecDescription: values.clausecDescription,
        platformType: currentShopPlatformType || '',
        shopId: state.currentShopId || 0,
        id: editing?.id,
        latestVersionId: editing?.latestVersionId,
      };
      const res = editing ? await editClauseApi(payload) : await addClauseApi(payload);
      if (res?.code === 200 || res?.code === 0) {
        message.success(editing ? '保存成功' : '新建成功');
        setCreateVisible(false);
        setEditing(null);
        fetchList({ pageNum: 1 });
      } else {
        message.error(res?.msg || (editing ? '保存失败' : '新建失败'));
      }
    } catch (error) {
      if ((error as any)?.errorFields) return;
      message.error(editing ? '保存失败' : '新建失败');
    }
  };

  const handleDelete = (record: any) => {
    Modal.confirm({
      title: '确认删除该条款？',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await deleteClauseApi(record.id);
          if (res?.code === 200 || res?.code === 0) {
            message.success('删除成功');
            fetchList({ pageNum: 1 });
          } else {
            message.error(res?.msg || '删除失败');
          }
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const handleEdit = async (record: any) => {
    try {
      const res = await getClauseDetailApi(record.id);
      if (res?.code === 200 || res?.code === 0) {
        const detail = res.data || res;
        setEditing(detail);
        createForm.setFieldsValue({
          clauseName: detail.clauseName,
          clausecDescription: detail.clausecDescription,
        });
        setCreateVisible(true);
      } else {
        message.error(res?.msg || '获取详情失败');
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleCreateVersion = (record: any) => {
    setVersionTarget(record);
    setVersionMode('create');
    versionForm.setFieldsValue({
      clauseName: record?.clauseName,
      versionId: record?.latestVersionId || '系统自动生成',
      description: '',
      status: false,
    });
    setVersionHtml('');
    setVersionVisible(true);
  };

  const handleSaveVersion = async () => {
    try {
      const values = await versionForm.validateFields();
      const plain = versionHtml
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, '')
        .trim();
      if (!versionHtml || !plain) {
        message.error('请输入条款内容');
        return;
      }
      const payload: any = {
        clauseId: versionTarget?.id,
        description: values.description,
        clauseContent: versionHtml,
        status: values.status ? 1 : 0,
      };
      let res;
      if (versionMode === 'edit') {
        payload.id = versionTarget?.versionRow?.id;
        payload.versionId = versionTarget?.versionRow?.versionId;
        res = await updateClauseVersionApi(payload);
      } else {
        res = await addClauseVersionApi(payload);
      }
      if (res?.code === 200 || res?.code === 0) {
        message.success(versionMode === 'edit' ? '保存成功' : '新建版本成功');
        handleCloseVersion();
        fetchList({ pageNum: 1 });
      } else {
        message.error(res?.msg || (versionMode === 'edit' ? '保存失败' : '新建版本失败'));
      }
    } catch (error) {
      if ((error as any)?.errorFields) return;
      message.error(versionMode === 'edit' ? '保存失败' : '新建版本失败');
    }
  };

  const handleCloseVersion = () => {
    setVersionVisible(false);
    setVersionTarget(null);
    setVersionHtml('');
    setVersionMode('create');
    versionForm.resetFields();
    if (versionEditor) {
      versionEditor.destroy?.();
      setVersionEditor(null);
    }
  };

  const handleViewVersions = (record: any) => {
    setVersionClause(record);
    versionFilterForm.resetFields();
    setVersionPageNum(1);
    setVersionPageSize(10);
    fetchVersionList({ pageNum: 1, clauseId: record?.id });
    setVersionListVisible(true);
  };

  const fetchVersionList = async (override?: Partial<{ pageNum: number; pageSize: number; clauseId: number }>) => {
    const values = versionFilterForm.getFieldsValue();
    const range = values.createTime || [];
    const params: any = {
      clauseId: override?.clauseId || versionClause?.id,
      versionId: values.versionId || '',
      status: values.status === 0 || values.status === 1 ? values.status : '',
      createTimeStart: range?.length ? dayjs(range[0]).format('YYYY-MM-DD HH:mm:ss') : '',
      createTimeEnd: range?.length ? dayjs(range[1]).format('YYYY-MM-DD HH:mm:ss') : '',
      pageNo: override?.pageNum || versionPageNum,
      pageSize: override?.pageSize || versionPageSize,
    };
    setVersionListLoading(true);
    try {
      const res = await getClauseVersionListApi(params);
      if (res?.code === 200 || res?.code === 0) {
        setVersionList(res.rows || res.data?.rows || []);
        setVersionTotal(res.total || res.data?.total || 0);
        if (override?.pageNum) setVersionPageNum(override.pageNum);
        if (override?.pageSize) setVersionPageSize(override.pageSize);
      } else {
        message.error(res?.msg || '获取版本列表失败');
      }
    } catch (error) {
      message.error('获取版本列表失败');
    } finally {
      setVersionListLoading(false);
    }
  };

  const handlePublish = async (row: any) => {
    Modal.confirm({
      title: '确认发布该版本？',
      onOk: async () => {
        try {
          const res = await publishClauseVersionApi({ clauseId: versionClause?.id, versionId: row.versionId });
          if (res?.code === 200 || res?.code === 0) {
            message.success('发布成功');
            fetchVersionList({ pageNum: 1 });
          } else {
            message.error(res?.msg || '发布失败');
          }
        } catch (error) {
          message.error('发布失败');
        }
      },
    });
  };

  const openVersionDetail = async (row: any, mode: 'view' | 'edit') => {
    try {
      const res = await getClauseVersionDetailApi(row.id);
      if (res?.code === 200 || res?.code === 0) {
        const detail = res.data || res;
        setVersionTarget({ ...versionClause, versionRow: row });
        setVersionMode(mode);
        versionForm.setFieldsValue({
          clauseName: versionClause?.clauseName,
          versionId: detail.versionId,
          description: detail.description,
          status: detail.status === 1,
        });
        setVersionHtml(detail.clauseContent || '');
        setVersionVisible(true);
      } else {
        message.error(res?.msg || '获取版本详情失败');
      }
    } catch (error) {
      message.error('获取版本详情失败');
    }
  };

  const handleDeleteVersion = (row: any) => {
    Modal.confirm({
      title: '确认删除该版本？',
      onOk: async () => {
        try {
          const res = await deleteClauseVersionApi(row.id);
          if (res?.code === 200 || res?.code === 0) {
            message.success('删除成功');
            fetchVersionList({ pageNum: 1 });
          } else {
            message.error(res?.msg || '删除失败');
          }
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  return (
    <div style={{ padding: 24, minHeight: '100%' }}>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12, columnGap: 12 }}>
        <Form.Item
          label="名称"
          name="clauseName">
          <Input
            placeholder="请输入名称"
            allowClear
            style={{ width: 200 }}
          />
        </Form.Item>
        <Form.Item
          label="创建时间"
          name="createTime">
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
          />
        </Form.Item>
        <Form.Item>
          <Space size={12}>
            <Button
              type="primary"
              onClick={() => fetchList({ pageNum: 1 })}>
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <div style={{ marginBottom: 12 }}>
        {checkPermissions(TERMS_KEY.ADD) && (
          <Button
            type="primary"
            onClick={() => {
              createForm.resetFields();
              setEditing(null);
              setCreateVisible(true);
            }}>
            新建条款
          </Button>
        )}
      </div>

      <Table
        rowKey={record => record.id || record.clauseName}
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条数据`,
          onChange: (pn, ps) => fetchList({ pageNum: pn, pageSize: ps }),
        }}
      />

      <Modal
        title={editing ? '编辑条款类型' : '新建条款类型'}
        open={createVisible}
        onCancel={() => {
          setCreateVisible(false);
          setEditing(null);
        }}
        onOk={handleCreate}
        destroyOnHidden>
        <Form
          layout="vertical"
          form={createForm}>
          <Form.Item
            label="名称"
            name="clauseName"
            rules={[{ required: true, message: '请输入名称' }]}>
            <Input
              placeholder="请输入名称(20字以内)"
              maxLength={20}
            />
          </Form.Item>
          <Form.Item
            label="描述"
            name="clausecDescription">
            <Input
              placeholder="请输入描述内容(100字以内)"
              maxLength={100}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={versionMode === 'edit' ? '编辑条款版本' : versionMode === 'view' ? '查看条款版本' : '新建条款版本'}
        open={versionVisible}
        onCancel={handleCloseVersion}
        onOk={versionMode === 'view' ? handleCloseVersion : handleSaveVersion}
        width={900}
        destroyOnHidden
        afterClose={handleCloseVersion}>
        <Form
          layout="vertical"
          form={versionForm}>
          <Form.Item
            label="条款类型"
            name="clauseName"
            rules={[{ required: true }]}>
            <Input
              disabled
              placeholder="条款类型"
            />
          </Form.Item>
          <Form.Item
            label="条款版本号"
            name="versionId">
            <Input
              disabled
              placeholder="系统自动生成"
            />
          </Form.Item>
          <Form.Item
            label="版本更新描述"
            name="description"
            rules={[{ required: true, message: '请输入版本描述' }]}>
            <Input.TextArea
              placeholder="请输入版本更新描述"
              rows={3}
              disabled={versionMode === 'view'}
            />
          </Form.Item>
          <Form.Item
            label="条款内容"
            required
            validateStatus={!versionHtml && versionMode !== 'view' ? 'error' : ''}
            help={!versionHtml && versionMode !== 'view' ? '请输入条款内容' : ''}>
            <div style={{ border: '1px solid #f0f0f0' }}>
              <Toolbar
                editor={versionEditor}
                defaultConfig={toolbarConfig}
                mode="default"
              />
              <div style={{ borderTop: '1px solid #f0f0f0', minHeight: 260 }}>
                <Editor
                  defaultConfig={{ ...editorConfig, readOnly: versionMode === 'view' }}
                  value={versionHtml}
                  onCreated={setVersionEditor}
                  onChange={ed => setVersionHtml(ed.getHtml())}
                  mode="default"
                  style={{ minHeight: 240 }}
                />
              </div>
            </div>
          </Form.Item>
          <Form.Item
            name="status"
            valuePropName="checked">
            <Checkbox disabled={versionMode === 'view'}>立即发布</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`查看版本 - ${versionClause?.clauseName || ''}`}
        open={versionListVisible}
        onCancel={() => setVersionListVisible(false)}
        footer={null}
        width={1000}
        destroyOnHidden>
        <Form
          form={versionFilterForm}
          layout="inline"
          style={{ marginBottom: 16, rowGap: 12, columnGap: 12 }}>
          <Form.Item
            label="版本号"
            name="versionId">
            <Input
              placeholder="请输入版本号"
              allowClear
            />
          </Form.Item>
          <Form.Item
            label="状态"
            name="status">
            <Select
              allowClear
              style={{ width: 160 }}
              placeholder="全部"
              options={[
                { label: '生效中', value: 1 },
                { label: '未生效', value: 0 },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="创建时间"
            name="createTime">
            <DatePicker.RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Form.Item>
          <Form.Item>
            <Space size={12}>
              <Button
                type="primary"
                onClick={() => fetchVersionList({ pageNum: 1 })}>
                查询
              </Button>
              <Button
                onClick={() => {
                  versionFilterForm.resetFields();
                  fetchVersionList({ pageNum: 1 });
                }}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
        <Table
          rowKey={record => record.id || record.versionId}
          loading={versionListLoading}
          dataSource={versionList}
          pagination={{
            current: versionPageNum,
            pageSize: versionPageSize,
            total: versionTotal,
            showSizeChanger: true,
            showTotal: t => `共 ${t} 条数据`,
            onChange: (pn, ps) => fetchVersionList({ pageNum: pn, pageSize: ps }),
          }}
          columns={[
            { title: '版本号', dataIndex: 'versionId' },
            { title: '创建人', dataIndex: 'createBy' },
            { title: '创建时间', dataIndex: 'createTime' },
            {
              title: '状态',
              dataIndex: 'status',
              render: (val: any) => (Number(val) === 1 ? '生效中' : '未生效'),
            },
            {
              title: '操作',
              render: (_: any, row: any) => (
                <Space size={12}>
                  {Number(row.status) === 1 ? (
                    <span style={{ color: '#999' }}>已发布</span>
                  ) : (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => handlePublish(row)}>
                      发布
                    </Button>
                  )}
                  <Button
                    type="link"
                    size="small"
                    onClick={() => openVersionDetail(row, 'view')}>
                    查看
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => openVersionDetail(row, 'edit')}>
                    编辑
                  </Button>
                  <Button
                    hidden
                    type="link"
                    size="small"
                    danger
                    onClick={() => handleDeleteVersion(row)}>
                    删除
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default TermsConditions;
